import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Crown, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useRoomSync } from "../lib/useRoomSync";

const AVATAR_MAP = {
  "pink-w":   { img: "/assets/home/avatar-pink-w.png",   ring: "#FF0F7B" },
  "orange-w": { img: "/assets/home/avatar-orange-w.png", ring: "#FFB800" },
  "purple-w": { img: "/assets/home/avatar-purple-w.png", ring: "#7B61FF" },
  "red-m":    { img: "/assets/home/avatar-red-m.png",    ring: "#FF0F7B" },
  "blue-m":   { img: "/assets/home/avatar-blue-m.png",   ring: "#00C2FF" },
  "green-m":  { img: "/assets/home/avatar-green-m.png",  ring: "#3DD68C" },
};
const av = (id) => AVATAR_MAP[id]?.img;
const ring = (id) => AVATAR_MAP[id]?.ring || "#FF2E9A";

const REACTION_EMOJIS = ["😂", "😭", "😱", "💀", "🔥", "🚩", "👏"];

/**
 * Confessions gameplay — typed answers, reactions, votes, leaderboard.
 *
 * Phases:
 *  - submit   : everyone types a confession; avatars tick as they submit.
 *  - reactions: list of confessions (public shows names, anonymous hides);
 *               tap emoji to react; public mode also gets up to 2 votes.
 *  - results  : winner highlight + leaderboard. Host taps Next.
 *  - finished : final leaderboard (after 10 rounds).
 */
export default function PlayGroupConfessions({ code, currentPlayerId, isHost, onLeave }) {
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  // SSE-backed group room sync (~500ms latency vs old 2s poll).
  const { data: room, refresh: fetchRoom } = useRoomSync({
    code,
    endpoint: "group-rooms",
    fetchFn: useCallback(() => api.getGroupRoom(code), [code]),
  });

  const state = room?.confession_state;
  const players = room?.players || [];

  const mySubmission = useMemo(
    () => (state?.submissions || []).find((s) => s.player_id === currentPlayerId),
    [state, currentPlayerId]
  );

  const submittedIds = useMemo(
    () => new Set((state?.submissions || []).map((s) => s.player_id)),
    [state]
  );

  // Pre-fill draft when re-entering submit phase
  useEffect(() => {
    if (state?.phase === "submit") setDraft(mySubmission?.text || "");
  }, [state?.phase, state?.round_number, mySubmission?.text]);

  const handleSubmit = async () => {
    const text = draft.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      await api.confessionSubmit(code, { player_id: currentPlayerId, text });
      await fetchRoom();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReact = async (confessionId, emoji) => {
    try {
      await api.confessionReact(code, { player_id: currentPlayerId, confession_id: confessionId, emoji });
      fetchRoom();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't react");
    }
  };

  const handleVote = async (confessionId) => {
    try {
      await api.confessionVote(code, { voter_id: currentPlayerId, confession_id: confessionId });
      fetchRoom();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't vote");
    }
  };

  const handleReveal = async () => {
    if (!isHost) return;
    setAdvancing(true);
    try {
      await api.confessionReveal(code);
      await fetchRoom();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reveal");
    } finally {
      setAdvancing(false);
    }
  };

  const handleNext = async () => {
    if (!isHost) return;
    setAdvancing(true);
    try {
      await api.confessionNext(code);
      await fetchRoom();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't advance");
    } finally {
      setAdvancing(false);
    }
  };

  if (!room || !state) {
    return (
      <div className="vc-bg min-h-screen flex items-center justify-center text-white/70" data-testid="confessions-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const isAnonymous = state.round_type === "anonymous";
  const accent = isAnonymous ? "#9B6BFF" : "#FF2E9A";
  const accent2 = isAnonymous ? "#6A4DFF" : "#FF6B47";

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="confessions-screen">
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          top: 40, left: "50%", transform: "translateX(-50%)",
          width: 460, height: 460,
          background: `radial-gradient(circle, ${accent}40 0%, ${accent2}1f 38%, transparent 70%)`,
          filter: "blur(50px)", zIndex: 0,
        }}
        aria-hidden
      />

      <main className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onLeave}
            aria-label="Leave"
            data-testid="confessions-leave"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
            style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div
            className="px-3 py-1 rounded-full text-[10px] font-black tracking-[0.22em]"
            style={{
              background: `${accent}1a`,
              border: `1.5px solid ${accent}`,
              color: "#fff",
              boxShadow: `0 0 12px ${accent}66`,
            }}
            data-testid="confessions-round-pill"
          >
            🤫 ROUND {state.round_number}/{state.total_rounds}
          </div>
          <div className="w-10" aria-hidden />
        </div>

        {/* Round type banner */}
        <div
          className="rounded-full px-3 py-1.5 mb-3 mx-auto text-[10.5px] font-black tracking-[0.18em] inline-flex items-center justify-center gap-1.5"
          style={{
            background: isAnonymous ? "rgba(155, 107, 255, 0.16)" : "rgba(255, 46, 154, 0.16)",
            border: `1px solid ${accent}80`,
            color: "#fff",
            alignSelf: "center",
          }}
          data-testid="confessions-round-type"
        >
          {isAnonymous ? <>🎭 ANONYMOUS ROUND — REACTIONS ONLY</> : <>👀 PUBLIC ROUND — VOTE YOUR FAVES</>}
        </div>

        {/* Title */}
        <h1
          className="font-extrabold leading-[1.0] tracking-tight text-center mb-2"
          style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: "clamp(1.7rem, 6.4vw, 2.1rem)",
            letterSpacing: "-0.02em",
            background: `linear-gradient(90deg, #FFE2F0 0%, ${accent} 55%, ${accent2} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            textShadow: `0 0 1px ${accent}99`,
            filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
          }}
          data-testid="confessions-title"
        >
          Confessions 🤫
        </h1>

        {/* Question card — universal vc-q system, tone follows round type */}
        <div
          className={`vc-q-card ${isAnonymous ? "vc-q-intimate" : "vc-q-spicy"} mb-4 mx-auto`}
          style={{ padding: "28px 22px 24px", maxWidth: "100%" }}
          data-testid="confessions-prompt-card"
        >
          <div className="vc-q-label" style={{ color: isAnonymous ? "#C9B7FF" : "#FFB3D1" }}>
            {isAnonymous ? "Anonymous · React only" : "Public · Vote up to 2"}
          </div>
          <p
            className="vc-q-text vc-q-gap-label-to-text"
            style={{ fontSize: "clamp(22px, 5.1vw, 30px)" }}
            data-testid="confessions-prompt"
          >
            {state.prompt}
          </p>
        </div>

        <AnimatePresence mode="wait">
          {state.phase === "submit" && (
            <motion.div key="submit" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              <SubmitPhase
                accent={accent}
                draft={draft}
                setDraft={setDraft}
                submitting={submitting}
                mySubmission={mySubmission}
                onSubmit={handleSubmit}
                players={players}
                submittedIds={submittedIds}
                isAnonymous={isAnonymous}
              />
            </motion.div>
          )}

          {state.phase === "reactions" && (
            <motion.div key="reactions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              <ReactionsPhase
                accent={accent}
                state={state}
                currentPlayerId={currentPlayerId}
                isAnonymous={isAnonymous}
                onReact={handleReact}
                onVote={handleVote}
                isHost={isHost}
                onReveal={handleReveal}
                advancing={advancing}
              />
            </motion.div>
          )}

          {(state.phase === "results" || state.phase === "finished") && (
            <motion.div key="results" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}>
              <ResultsPhase
                accent={accent}
                state={state}
                players={players}
                isAnonymous={isAnonymous}
                isHost={isHost}
                onNext={handleNext}
                advancing={advancing}
                isFinal={state.phase === "finished"}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex-1" />
      </main>
    </div>
  );
}

// ---------------------------------------------------------- SUBMIT PHASE --
function SubmitPhase({ accent, draft, setDraft, submitting, mySubmission, onSubmit, players, submittedIds, isAnonymous }) {
  const remaining = 140 - draft.length;
  const locked = !!mySubmission;
  return (
    <>
      <div
        className="rounded-2xl p-4 mb-3"
        style={{
          background: "rgba(15, 8, 22, 0.7)",
          border: `1.5px solid ${accent}80`,
          boxShadow: `0 0 18px ${accent}33`,
        }}
      >
        <div className="text-[10px] font-black tracking-[0.22em] mb-2" style={{ color: accent }}>
          {isAnonymous ? "ANONYMOUS — NO NAMES SHOWN" : "PUBLIC — NAME WILL BE SHOWN"}
        </div>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 140))}
          placeholder="Type your confession…"
          disabled={locked}
          data-testid="confessions-input"
          className="w-full bg-transparent text-white text-[15.5px] font-bold placeholder:text-white/40 focus:outline-none resize-none"
          rows={3}
        />
        <div className="flex items-center justify-between mt-1">
          <span className="text-white/40 text-[11px] font-semibold">{remaining} chars left</span>
          {locked && <span className="text-[#7DE6A9] text-[11px] font-extrabold">✓ Locked in</span>}
        </div>
      </div>

      <button
        onClick={onSubmit}
        disabled={submitting || locked || !draft.trim()}
        data-testid="confessions-submit"
        className="vc-cta vc-cta-primary w-full rounded-full py-[15px] font-extrabold inline-flex items-center justify-center gap-2 mb-5"
        style={{ fontSize: "16px" }}
      >
        {locked ? "Waiting for everyone else…" : submitting ? "Sending…" : (<>Submit confession <Send className="w-4 h-4" /></>)}
      </button>

      {/* Player tick-row */}
      <div className="text-white/60 text-[11px] font-semibold mb-1.5 text-center" data-testid="confessions-submit-progress">
        {submittedIds.size} of {players.length} submitted
      </div>
      <div className="flex items-center justify-center gap-2 flex-wrap">
        {players.map((p) => {
          const done = submittedIds.has(p.id);
          return (
            <div key={p.id} className="relative" data-testid={`confessions-player-${p.id}-${done ? "done" : "pending"}`}>
              <div
                className="w-9 h-9 rounded-full overflow-hidden"
                style={{
                  border: `2px solid ${done ? "#3DD68C" : ring(p.avatar_id) + "60"}`,
                  boxShadow: done ? "0 0 8px rgba(61, 214, 140, 0.7)" : "none",
                  opacity: done ? 1 : 0.45,
                }}
              >
                {av(p.avatar_id) ? (
                  <img src={av(p.avatar_id)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-[12px] font-extrabold" style={{ background: ring(p.avatar_id) + "33" }}>
                    {(p.name || "?")[0].toUpperCase()}
                  </div>
                )}
              </div>
              {done && (
                <span
                  className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full text-[9px] font-black flex items-center justify-center text-black"
                  style={{ background: "#3DD68C", boxShadow: "0 0 4px rgba(61, 214, 140, 0.7)" }}
                >
                  ✓
                </span>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ------------------------------------------------------- REACTIONS PHASE --
function ReactionsPhase({ accent, state, currentPlayerId, isAnonymous, onReact, onVote, isHost, onReveal, advancing }) {
  const submissions = state.submissions || [];
  const reactions = state.reactions || {};
  const votes = state.votes || {};
  const myVotes = new Set(votes[currentPlayerId] || []);
  const votesLeft = 2 - myVotes.size;

  return (
    <>
      <div className="space-y-2.5 mb-4" data-testid="confessions-list">
        {submissions.map((s, idx) => {
          const myRxn = (reactions[s.id] || {})[currentPlayerId];
          const allRxns = reactions[s.id] || {};
          const counts = {};
          for (const e of Object.values(allRxns)) counts[e] = (counts[e] || 0) + 1;
          const totalRxns = Object.values(counts).reduce((a, b) => a + b, 0);
          const voted = myVotes.has(s.id);
          const isMine = s.player_id === currentPlayerId;
          return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.04 }}
              className="rounded-2xl p-3"
              style={{
                background: voted ? `${accent}1a` : "rgba(15, 8, 22, 0.65)",
                border: `1.5px solid ${voted ? accent : `${accent}45`}`,
                boxShadow: voted ? `0 0 16px ${accent}66` : "none",
              }}
              data-testid={`confessions-card-${s.id}`}
            >
              <div className="flex items-start gap-2.5">
                {/* Avatar (anonymous = silhouette mask) */}
                <div
                  className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0"
                  style={{
                    border: `2px solid ${isAnonymous ? `${accent}aa` : ring(s.avatar_id)}`,
                    boxShadow: `0 0 6px ${isAnonymous ? accent : ring(s.avatar_id)}88`,
                  }}
                >
                  {isAnonymous ? (
                    <div className="w-full h-full flex items-center justify-center text-[20px]" style={{ background: `${accent}22` }}>
                      🎭
                    </div>
                  ) : av(s.avatar_id) ? (
                    <img src={av(s.avatar_id)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white text-[12px] font-extrabold" style={{ background: ring(s.avatar_id) + "33" }}>
                      {(s.name || "?")[0].toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-extrabold tracking-wide mb-0.5" style={{ color: accent }}>
                    {isAnonymous ? "Anonymous" : s.name}
                    {!isAnonymous && isMine && <span className="text-white/50 ml-1">(you)</span>}
                  </div>
                  <p className="text-white text-[14px] font-bold leading-snug">{s.text}</p>
                </div>
              </div>

              {/* Reaction row */}
              <div className="flex items-center gap-1 mt-2.5 flex-wrap" data-testid={`confessions-react-${s.id}`}>
                {REACTION_EMOJIS.map((e) => {
                  const selected = myRxn === e;
                  const c = counts[e] || 0;
                  return (
                    <button
                      key={e}
                      onClick={() => onReact(s.id, e)}
                      data-testid={`confessions-react-${s.id}-${e}`}
                      className="px-2 py-1 rounded-full text-[13px] font-bold inline-flex items-center gap-1 transition active:scale-90"
                      style={{
                        background: selected ? `${accent}33` : "rgba(255, 255, 255, 0.05)",
                        border: `1px solid ${selected ? accent : "rgba(255, 255, 255, 0.1)"}`,
                      }}
                    >
                      <span>{e}</span>
                      {c > 0 && <span className="text-white/85 text-[11px] font-extrabold">{c}</span>}
                    </button>
                  );
                })}
                <span className="text-white/40 text-[10.5px] font-semibold ml-auto">
                  {totalRxns} rxn{totalRxns !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Vote button — public only, can't vote for yourself */}
              {!isAnonymous && !isMine && (
                <button
                  onClick={() => onVote(s.id)}
                  data-testid={`confessions-vote-${s.id}`}
                  disabled={!voted && votesLeft <= 0}
                  className="w-full mt-2 rounded-full py-1.5 text-[12px] font-extrabold inline-flex items-center justify-center gap-1 transition active:scale-95 disabled:opacity-40"
                  style={{
                    background: voted ? `linear-gradient(90deg, ${accent}, #FF6B47)` : "rgba(255, 255, 255, 0.04)",
                    color: voted ? "#fff" : "#fff",
                    border: `1px solid ${voted ? accent : "rgba(255, 255, 255, 0.12)"}`,
                  }}
                >
                  {voted ? "★ Voted" : "Vote favorite"}
                </button>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Bottom action */}
      {!isAnonymous && (
        <div className="text-center text-white/65 text-[12px] font-bold mb-2" data-testid="confessions-vote-status">
          Votes left: <span style={{ color: accent }}>{votesLeft}</span> / 2
        </div>
      )}
      {isHost ? (
        <button
          onClick={onReveal}
          disabled={advancing}
          data-testid="confessions-reveal"
          className="vc-cta vc-cta-primary w-full rounded-full py-[15px] font-extrabold inline-flex items-center justify-center gap-2"
          style={{ fontSize: "16px" }}
        >
          {advancing ? "Revealing…" : (<>Reveal winner <ArrowRight className="w-5 h-5" strokeWidth={3} /></>)}
        </button>
      ) : (
        <div
          className="rounded-2xl py-3 px-4 text-center"
          style={{ background: "rgba(20, 14, 30, 0.55)", border: `1.5px solid ${accent}66` }}
          data-testid="confessions-guest-wait"
        >
          <span className="text-white text-[13px] font-bold">React + vote — waiting for host to reveal…</span>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------- RESULTS PHASE --
function ResultsPhase({ accent, state, players, isAnonymous, isHost, onNext, advancing, isFinal }) {
  const winner = state.winner;
  const leaderboard = state.leaderboard || {};
  // Build full leaderboard rows (include all players, sorted by score desc)
  const rows = players
    .map((p) => ({
      id: p.id,
      name: p.name,
      avatar_id: p.avatar_id,
      points: leaderboard[p.id] || 0,
    }))
    .sort((a, b) => b.points - a.points);

  return (
    <>
      <h2
        className="text-center font-extrabold mb-2"
        style={{
          fontFamily: "'Manrope', sans-serif",
          fontSize: "20px",
          color: "#fff",
          filter: `drop-shadow(0 0 10px ${accent}88)`,
        }}
        data-testid="confessions-results-title"
      >
        {isFinal ? "Game Over 👑" : isAnonymous ? "Most reacted… 🔥" : "The room voted… 🏆"}
      </h2>

      {winner && !isFinal && (
        <div className="flex flex-col items-center mb-4" data-testid="confessions-winner">
          <Crown className="w-9 h-9 mb-2" style={{ color: accent, filter: `drop-shadow(0 0 10px ${accent})` }} fill={accent} />
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
            className="w-[110px] h-[110px] rounded-full overflow-hidden"
            style={{ border: `3.5px solid ${accent}`, boxShadow: `0 0 28px ${accent}aa` }}
            data-testid="confessions-winner-avatar"
          >
            {av(winner.avatar_id) ? (
              <img src={av(winner.avatar_id)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white text-3xl font-extrabold" style={{ background: `${accent}33` }}>
                {(winner.name || "?")[0].toUpperCase()}
              </div>
            )}
          </motion.div>
          <div className="mt-2.5 text-white text-[19px] font-extrabold" data-testid="confessions-winner-name">{winner.name}</div>
          <div className="text-white/70 text-[12.5px] mt-1 px-4 text-center italic" data-testid="confessions-winner-text">
            &ldquo;{winner.text}&rdquo;
          </div>
          <div
            className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 rounded-full text-[11.5px] font-extrabold"
            style={{
              background: `linear-gradient(90deg, ${accent}, #FF6B47)`,
              color: "#fff",
              boxShadow: `0 0 10px ${accent}88`,
            }}
            data-testid="confessions-winner-score"
          >
            {isAnonymous
              ? `🔥 ${winner.total_reactions} reactions · +${winner.score + (winner.bonus || 0)} pts`
              : `🏆 ${winner.votes} votes · +${winner.score + (winner.bonus || 0)} pts`}
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div
        className="rounded-2xl p-3 mb-4"
        style={{
          background: "rgba(15, 8, 22, 0.7)",
          border: `1.5px solid ${accent}55`,
          boxShadow: `0 0 14px ${accent}33`,
        }}
        data-testid="confessions-leaderboard"
      >
        <div className="text-[10.5px] font-black tracking-[0.22em] mb-2" style={{ color: accent }}>
          🏆 LEADERBOARD
        </div>
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div
              key={r.id}
              className="flex items-center gap-2.5 rounded-xl px-2.5 py-1.5"
              style={{ background: i === 0 ? `${accent}1f` : "rgba(255, 255, 255, 0.03)", border: `1px solid ${i === 0 ? `${accent}80` : "transparent"}` }}
              data-testid={`confessions-leaderboard-row-${r.id}`}
            >
              <span className="text-white/55 text-[12px] font-black w-4 text-center">{i + 1}</span>
              <div
                className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0"
                style={{ border: `2px solid ${ring(r.avatar_id)}` }}
              >
                {av(r.avatar_id) ? (
                  <img src={av(r.avatar_id)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white text-[10px] font-extrabold" style={{ background: ring(r.avatar_id) + "33" }}>
                    {(r.name || "?")[0].toUpperCase()}
                  </div>
                )}
              </div>
              <span className="flex-1 text-white text-[13px] font-extrabold truncate">{r.name}</span>
              <span className="text-[12.5px] font-black" style={{ color: i === 0 ? accent : "#fff" }}>{r.points} pts</span>
            </div>
          ))}
        </div>
      </div>

      {isHost && !isFinal && (
        <button
          onClick={onNext}
          disabled={advancing}
          data-testid="confessions-next"
          className="vc-cta vc-cta-primary w-full rounded-full py-[15px] font-extrabold inline-flex items-center justify-center gap-2"
          style={{ fontSize: "16px" }}
        >
          {advancing ? "Loading…" : (<>Next confession <ArrowRight className="w-5 h-5" strokeWidth={3} /></>)}
        </button>
      )}
      {isHost && isFinal && (
        <button
          onClick={onNext}
          data-testid="confessions-finish"
          className="vc-cta vc-cta-primary w-full rounded-full py-[15px] font-extrabold inline-flex items-center justify-center gap-2"
          style={{ fontSize: "16px" }}
        >
          🎉 GG
        </button>
      )}
      {!isHost && !isFinal && (
        <div
          className="rounded-2xl py-3 px-4 text-center"
          style={{ background: "rgba(20, 14, 30, 0.55)", border: `1.5px solid ${accent}66` }}
          data-testid="confessions-guest-wait-next"
        >
          <span className="text-white text-[13px] font-bold">Waiting for host to start the next round…</span>
        </div>
      )}
    </>
  );
}
